import { FloatingDelayGroup } from '@floating-ui/react';
import React, { memo, useRef, useState } from 'react';
import { ContentCreationAddBlockButton } from '../add-block-button';
import { TextBoldNeueIcon } from 'src/monet/icons';
import { Nullable } from 'src/types/nullable.type';
import { useEditorStore } from '../editor-store';
import { PlaceHolderIcon } from '../helpers/placeholders';
import { CreateToolbarMenu } from './menu';
import {
  hasESignatureBlockOrPlaceholderBlock,
  hasNativeFormBlockOrPlaceholderBlock,
  isReadonly,
} from '../native-e-signature/utils';
import { v4 as uuidv4 } from 'uuid';

const generateUuid = () => uuidv4().replace(/-/g, '');

export const ContentCreateBar = memo(() => {
  const [showMenu, setShowMenu] = useState(false);
  const isBlockDragging = useRef(false);
  const [recentUserItem, setRecentUserItem] = useState<Nullable<any>>(null);

  const layoutStage = useEditorStore((state) => state.layoutStage);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);

  const sections = useEditorStore((state) => state.layout.sections);
  const readonly = isReadonly(sections);

  const hasESignatureBlock = hasESignatureBlockOrPlaceholderBlock(sections);
  const hasNativeFormBlock = hasNativeFormBlockOrPlaceholderBlock(sections);

  const onBlockDragStart = () => {
    isBlockDragging.current = true;
  };

  const onBlockDragEnd = () => {
    isBlockDragging.current = false;
  };

  return (
    <div
      className='pointer-events-auto w-12 flex flex-row-reverse transition rounded-lg bg-neue-journey-bg text-neue-journey-fg gap-2 p-2'
      style={{ opacity: layoutStage === 'ready' ? 1 : 0 }}
    >
      <div id='journey-toolbar' className='flex flex-col gap-1.5 pointer-events-none'>
        <FloatingDelayGroup delay={{ open: 10 }}>
          <ContentCreationAddBlockButton
            disabled={readonly}
            blockContent={{ type: 'text', value: '<p></p>', horizontalAlignment: 'left' }}
            tooltip='Text'
            onDrag={onBlockDragStart}
            onDrop={onBlockDragEnd}
          >
            <TextBoldNeueIcon />
          </ContentCreationAddBlockButton>
          <ContentCreationAddBlockButton
            disabled={readonly || hasNativeFormBlock}
            blockContent={{
              type: 'native-form',
              formUUID: generateUuid(),
              config: {
                title: 'Native Form',
                description: 'Native form description',
                submitButtonText: 'Submit',
                submitButtonAlignment: 'left',
              },
              fields: [
                {
                  type: 'input',
                  id: generateUuid(),
                  inputType: 'text',
                  description: 'Input field description',
                  label: 'Input Field',
                  value: '',
                  required: false,
                  uuid: generateUuid(),
                  position: 0,
                },
              ],
            }}
            tooltip='Native form'
            onDrag={onBlockDragStart}
            onDrop={onBlockDragEnd}
          >
            <PlaceHolderIcon blockPlaceholderType='native-form' />
          </ContentCreationAddBlockButton>
          <ContentCreationAddBlockButton
            disabled={readonly}
            blockContent={{ type: 'placeholder', placeholderType: 'add-image' }}
            tooltip='Image'
            onDrag={onBlockDragStart}
            onDrop={onBlockDragEnd}
          >
            <PlaceHolderIcon blockPlaceholderType='add-image' />
          </ContentCreationAddBlockButton>
          <ContentCreationAddBlockButton
            joyrideClassname='journey-link-toolbar-option'
            disabled={readonly}
            blockContent={{ type: 'placeholder', placeholderType: 'add-link' }}
            tooltip='Link'
            onDrag={onBlockDragStart}
            onDrop={onBlockDragEnd}
          >
            <PlaceHolderIcon blockPlaceholderType='add-link' />
          </ContentCreationAddBlockButton>
          <ContentCreationAddBlockButton
            disabled={readonly}
            blockContent={{ type: 'placeholder', placeholderType: 'add-file' }}
            tooltip='Upload'
            onDrag={onBlockDragStart}
            onDrop={onBlockDragEnd}
          >
            <PlaceHolderIcon blockPlaceholderType='add-file' />
          </ContentCreationAddBlockButton>
          <ContentCreationAddBlockButton
            disabled={readonly}
            blockContent={{ type: 'placeholder', placeholderType: 'add-media' }}
            tooltip='Library'
            onDrag={onBlockDragStart}
            onDrop={onBlockDragEnd}
          >
            <PlaceHolderIcon blockPlaceholderType='add-media' />
          </ContentCreationAddBlockButton>
          <ContentCreationAddBlockButton
            disabled={readonly}
            blockContent={{ type: 'placeholder', placeholderType: 'add-recording' }}
            tooltip='Recording'
          >
            <PlaceHolderIcon blockPlaceholderType='add-recording' />
          </ContentCreationAddBlockButton>
          <div className='h-px mx-[7px] w-5 transition-colors bg-neue-journey-fg-20' />
          {recentUserItem &&
            (() => {
              const { content, label, Icon } = recentUserItem;
              let disabled = false;
              if (content.type === 'placeholder' && content.placeholderType === 'add-native-e-signature') {
                disabled = hasESignatureBlock;
              }
              return (
                <ContentCreationAddBlockButton blockContent={content} tooltip={label} disabled={readonly || disabled}>
                  <Icon className='w-5 h-5 m-auto' />
                </ContentCreationAddBlockButton>
              );
            })()}
        </FloatingDelayGroup>
        <CreateToolbarMenu
          disabled={readonly}
          showMenu={showMenu}
          onAddItem={setRecentUserItem}
          onOpen={() => {
            clearSelectedBlock();
            setShowMenu(true);
          }}
          onClose={() => {
            setShowMenu(false);
          }}
        />
      </div>
    </div>
  );
});
